import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    CircularProgress,
    TextField,
    Typography,
    Box,
    Tab,
    Tabs,
    Chip,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Fab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination
} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import { v4 as uuid } from 'uuid';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faCalculator, faTimes, faSync, faUndo} from "@fortawesome/free-solid-svg-icons";
import {Button} from "@material-ui/core";

import { AuthenticationService } from "../../../Services/AuthenticationService";
import { parseNumberInputValue } from "../../../utils/numberInputUtils";
import { getPartParentRelations } from '../../../utils/bomContributions';
const styles = theme => ({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    selectButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    filterToggle: {
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '2px 10px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        minWidth: '150px',
        '& .MuiCheckbox-root': {
            padding: '0px',
            color: '#35d2d9',
            '&.Mui-checked': {
                color: '#35d2d9',
            },
        }
    },
    filterLabel: {
        marginLeft: '8px',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.87)',
    },
    floatingConfirmButton: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000,
    },
    floatingResetButton: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000,
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 0',
        borderTop: '1px solid #e0e0e0',
        marginTop: '16px'
    },
    paginationControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    }
});

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`inventory-setup-tabpanel-${index}`}
            aria-labelledby={`inventory-setup-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

class InventorySetup extends React.Component {
    constructor(props) {
        super(props);
        globalProps = props;
    }

    state = {
        loading: true,
        user: {},
        assemblies: [],
        subAssemblies: [],
        parts: [],
        activeTab: 0,
        modifiedRows: {},
        confirmedRows: {},
        savingRows: {},
        selectedRows: {},
        searchTerm: '',
        // Calculator modal state
        calculatorOpen: false,
        calculatorRowKey: null,
        calculatorTitle: '',
        calculatorLines: [0],
        calculatorTotal: 0,
        hideConfirmedRows: false,
        showNonInventoryItems: false,
        collaborativeState: {
            packageLogs: [],
            inventoryLogs: [],
            lastRefresh: null
        },
        // Track if this is the first load to store original quantities
        isFirstLoad: true,
        // Track last data refresh time
        lastDataRefresh: null,
        // Track manually modified rows (user input only, not sync updates)
        manuallyModifiedRows: {},
        // Pagination state
        page: 0,
        rowsPerPage: 100,
        rowsPerPageOptions: [50, 100, 250, 500]
    };

    componentDidMount() {
        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
        
        // Check if mobile and redirect
        if (window.innerWidth < 960) {
            const sessionId = this.getSessionId();
            globalProps.history.push(`/inventory/session/${sessionId}`);
            return;
        }
        
        this.loadStoredState();
        this.loadData();
        this.startCollaborativeRefresh();
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    }

    loadStoredState = () => {
        const storedState = localStorage.getItem('inventorySetupState' + this.getSessionId());
        if (storedState) {
            try {
                const parsed = JSON.parse(storedState);
                
                // Migrate old format keys to new format
                const migrateKeys = (obj) => {
                    const migrated = {};
                    Object.keys(obj).forEach(key => {
                        migrated[key] = obj[key];
                    });
                    return migrated;
                };
                
                this.setState({
                    modifiedRows: migrateKeys(parsed.modifiedRows || {}),
                    confirmedRows: migrateKeys(parsed.confirmedRows || {}),
                    activeTab: parsed.activeTab || 0,
                    hideConfirmedRows: parsed.hideConfirmedRows || false,
                    showNonInventoryItems: parsed.showNonInventoryItems || false,
                    manuallyModifiedRows: migrateKeys(parsed.manuallyModifiedRows || {}),
                    page: parsed.page || 0,
                    rowsPerPage: parsed.rowsPerPage || 100,
                    searchTerm: parsed.searchTerm || ''
                });
            } catch (e) {
                console.warn('Failed to parse stored inventory setup state:', e);
            }
        }
    };

    saveStateToStorage = () => {
        const stateToSave = {
            modifiedRows: this.state.modifiedRows,
            confirmedRows: this.state.confirmedRows,
            activeTab: this.state.activeTab,
            hideConfirmedRows: this.state.hideConfirmedRows,
            showNonInventoryItems: this.state.showNonInventoryItems,
            manuallyModifiedRows: this.state.manuallyModifiedRows,
            page: this.state.page,
            rowsPerPage: this.state.rowsPerPage,
            searchTerm: this.state.searchTerm
        };
        localStorage.setItem('inventorySetupState' + this.getSessionId(), JSON.stringify(stateToSave));
    };

    startCollaborativeRefresh = () => {
        this.loadCollaborativeState();
        this.refreshTimer = setInterval(() => {
            this.loadCollaborativeState();
            // Also refresh main data every 30 seconds
            this.loadData();
        }, 60000); // Refresh every 30 seconds
    };

    loadCollaborativeState = async () => {
        try {
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const partService = ServiceLocator.Instance.getPartService();
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            
            // Load package logs for assemblies and subassemblies with session-specific reason
            const packageLogs = await assemblyService.getPackageLogs();
            const initialPackageLogs = packageLogs.filter(log => 
                log.reason === sessionReason
            );
            
            // Load inventory logs for parts with session-specific reason
            const inventoryLogs = await partService.getInventoryLogs();
            const initialInventoryLogs = inventoryLogs.filter(log => 
                log.reason === sessionReason
            );

            this.setState({
                collaborativeState: {
                    packageLogs: initialPackageLogs,
                    inventoryLogs: initialInventoryLogs,
                    lastRefresh: new Date()
                }
            });

            this.syncCollaborativeStateWithLocal();
        } catch (error) {
            console.warn('Failed to load collaborative state:', error);
        }
    };

    syncCollaborativeStateWithLocal = () => {
        const { packageLogs, inventoryLogs } = this.state.collaborativeState;
        const confirmedRows = { ...this.state.confirmedRows };

        // Mark rows as confirmed based on package logs
        packageLogs.forEach(log => {
            if (log.assemblyId) {
                            const rowKey = `assembly|${log.assemblyId}`;
            confirmedRows[rowKey] = true;
            }
            if (log.subAssemblyId) {
                            const rowKey = `subassembly|${log.subAssemblyId}`;
            confirmedRows[rowKey] = true;
            }
        });

        // Mark rows as confirmed based on inventory logs
        inventoryLogs.forEach(log => {
            const rowKey = `part|${log.partId}`;
            confirmedRows[rowKey] = true;
        });

        this.setState({ confirmedRows });
    };

    // Determine if an item is a child of another item
    isChildItem = (itemType, itemId) => {
        const { assemblies, subAssemblies } = this.state;
        
        if (itemType === 'assembly') {
            // Assemblies are never children
            return false;
        }
        
        if (itemType === 'subassembly') {
            // Check if subassembly is child of any assembly
            for (const assembly of assemblies) {
                if (assembly.assembly.subAssembliesToAssemblies?.some(sa => sa.subAssemblyFk === itemId)) {
                    return true;
                }
            }
            // Check if subassembly is child of any other subassembly
            for (const subAssembly of subAssemblies) {
                if (subAssembly.subAssembly.childSubAssemblies?.some(cs => cs.childSubAssemblyFk === itemId)) {
                    return true;
                }
            }
            return false;
        }
        
        if (itemType === 'part') {
            return getPartParentRelations(itemId, assemblies, subAssemblies).length > 0;
        }
        
        return false;
    };

    // Check if all parents of an item are confirmed
    areParentsConfirmed = (itemType, itemId) => {
        const { assemblies, subAssemblies } = this.state;
        
        if (itemType === 'assembly') {
            // Assemblies have no parents
            return true;
        }
        
        if (itemType === 'subassembly') {
            let hasParents = false;
            let allParentsConfirmed = true;
            const parentsList = [];
            
            // Check assembly parents
            for (const assembly of assemblies) {
                if (assembly.assembly.subAssembliesToAssemblies?.some(sa => sa.subAssemblyFk === itemId)) {
                    hasParents = true;
                    const parentRowKey = `assembly|${assembly.assembly.id}`;
                    const isConfirmed = this.isRowConfirmed(parentRowKey);
                    parentsList.push({type: 'assembly', id: assembly.assembly.assemblyId, confirmed: isConfirmed});
                    if (!isConfirmed) {
                        allParentsConfirmed = false;
                    }
                }
            }
            
            // Check subassembly parents
            for (const subAssembly of subAssemblies) {
                if (subAssembly.subAssembly.childSubAssemblies?.some(cs => cs.childSubAssemblyFk === itemId)) {
                    hasParents = true;
                    const parentRowKey = `subassembly|${subAssembly.subAssembly.id}`;
                    const isConfirmed = this.isRowConfirmed(parentRowKey);
                    parentsList.push({type: 'subassembly', id: subAssembly.subAssembly.subAssemblyId, confirmed: isConfirmed});
                    if (!isConfirmed) {
                        allParentsConfirmed = false;
                    }
                }
            }
            

            
            // If no parents found, show it (top-level subassembly)
            // If has parents, only show if ALL are confirmed
            return !hasParents || allParentsConfirmed;
        }
        
        if (itemType === 'part') {
            const parents = getPartParentRelations(itemId, assemblies, subAssemblies);
            if (parents.length === 0) {
                return true;
            }
            return parents.every((parent) => {
                const parentRowKey = `${parent.type}|${parent.id}`;
                return this.isRowConfirmed(parentRowKey);
            });
        }
        
        return true;
    };

    toggleHideConfirmedRows = () => {
        const { hideConfirmedRows } = this.state;
        this.setState({ 
            hideConfirmedRows: !hideConfirmedRows
        }, () => {
            this.saveStateToStorage();
        });
    };

    toggleShowNonInventoryItems = () => {
        const { showNonInventoryItems } = this.state;
        this.setState({ 
            showNonInventoryItems: !showNonInventoryItems
        }, () => {
            this.saveStateToStorage();
        });
    };

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user.isAdministrator) {
            AuthenticationService.getInstance().denyNonAdministratorAccess();
            return;
        }

        self.setState({syncing: true});
        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts()]).then(() => {
            self.setState({
                loading: false,
                syncing: false, 
                user,
                isFirstLoad: false,
                lastDataRefresh: new Date()
            });
        });
    };

    syncDataFromDatabase = async () => {
        try {
            this.setState({ syncing: true });

            // Preserve view state while clearing row-level changes
            const preservedState = {
                modifiedRows: {},
                confirmedRows: {},
                manuallyModifiedRows: {},
                activeTab: this.state.activeTab,
                hideConfirmedRows: this.state.hideConfirmedRows,
                showNonInventoryItems: this.state.showNonInventoryItems,
                page: this.state.page,
                rowsPerPage: this.state.rowsPerPage,
                searchTerm: this.state.searchTerm
            };
            localStorage.setItem('inventorySetupState' + this.getSessionId(), JSON.stringify(preservedState));
            // Clear cached data timestamps so services fetch fresh data
            localStorage.removeItem('assembliesUpdated');
            localStorage.removeItem('subAssembliesUpdated');
            localStorage.removeItem('partsUpdated');
            window.location.reload();
        /*
            
            // Clear localStorage cache to force fresh data
            localStorage.removeItem('assembliesUpdated');
            localStorage.removeItem('subAssembliesUpdated');
            localStorage.removeItem('partsUpdated');
            
            // Fetch fresh data from services
            const assemblyService = ServiceLocator.Instance.getAssemblyService();
            const partService = ServiceLocator.Instance.getPartService();
            
            const [assemblies, subAssemblies, parts] = await Promise.all([
                assemblyService.getAllAssembliesExpanded(),
                assemblyService.getAllSubAssembliesExpanded(),
                partService.getAllPartsExpanded()
            ]);
            
            // Update modifiedRows with new data while preserving user state
            const modifiedRows = { ...this.state.modifiedRows };
            
            // Update assemblies
            assemblies.forEach(assembly => {
                const key = `assembly|${assembly.assembly.id}`;
                const currentQuantity = assembly.totalAssembledOnHandWithRev;
                
                if (modifiedRows[key]) {
                    if (this.state.confirmedRows[key]) {
                        // If confirmed, update both original and current values
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag
                        const manuallyModifiedRows = { ...this.state.manuallyModifiedRows };
                        delete manuallyModifiedRows[key];
                        this.setState({ manuallyModifiedRows });
                    } else {
                        // If not confirmed, update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            
            // Update subassemblies
            subAssemblies.forEach(subAssembly => {
                const key = `subassembly|${subAssembly.subAssembly.id}`;
                const currentQuantity = subAssembly.totalAssembledOnHandWithRev;
                
                if (modifiedRows[key]) {
                    if (this.state.confirmedRows[key]) {
                        // If confirmed, update both original and current values
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag
                        const manuallyModifiedRows = { ...this.state.manuallyModifiedRows };
                        delete manuallyModifiedRows[key];
                        this.setState({ manuallyModifiedRows });
                    } else {
                        // If not confirmed, update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            
            // Update parts
            parts.forEach(part => {
                const key = `part|${part.part.id}`;
                const currentQuantity = part.totalUnpackaged;
                
                if (modifiedRows[key]) {
                    if (this.state.confirmedRows[key]) {
                        // If confirmed, update both original and current values
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                        // Clear manual modification flag
                        const manuallyModifiedRows = { ...this.state.manuallyModifiedRows };
                        delete manuallyModifiedRows[key];
                        this.setState({ manuallyModifiedRows });
                    } else {
                        // If not confirmed, update both current and original values since sync represents new baseline
                        modifiedRows[key] = {
                            originalValue: currentQuantity,
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            
            // Update state with new data
            this.setState({
                assemblies: assemblies.filter(x => x.assembly.isActive),
                subAssemblies: subAssemblies.filter(x => x.subAssembly.isActive),
                parts: parts.filter(x => x.part.isActive),
                modifiedRows,
                syncing: false,
                lastDataRefresh: new Date()
            }, () => {
                this.saveStateToStorage();
            });
            
            // Refresh collaborative state
            this.loadCollaborativeState();
            
            window.$alertSuccess('Data synced successfully');
            */
        } catch (error) {
            console.error('Error syncing data:', error);
            window.$alertError('Error syncing data');
            this.setState({ syncing: false });
        }
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            assemblies.forEach(assembly => {
                const key = `assembly|${assembly.assembly.id}`;
                const currentQuantity = assembly.totalAssembledOnHandWithRev;
                
                if (!modifiedRows[key]) {
                    // First time loading this assembly - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({assemblies: assemblies.filter(x => x.assembly.isActive), modifiedRows});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            subAssemblies.forEach(subAssembly => {
                const key = `subassembly|${subAssembly.subAssembly.id}`;
                const currentQuantity = subAssembly.totalAssembledOnHandWithRev;
                
                if (!modifiedRows[key]) {
                    // First time loading this subassembly - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({subAssemblies: subAssemblies.filter(x => x.subAssembly.isActive), modifiedRows});
        });
    };

    loadParts = () => {
        const self = this;
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            const modifiedRows = {...self.state.modifiedRows};
            const isFirstLoad = self.state.isFirstLoad;
            
            parts.forEach(part => {
                const key = `part|${part.part.id}`;
                const currentQuantity = part.totalUnpackaged;
                
                if (!modifiedRows[key]) {
                    // First time loading this part - store original quantity
                    modifiedRows[key] = {
                        originalValue: currentQuantity,
                        currentValue: currentQuantity
                    };
                } else if (!isFirstLoad) {
                    // Not first load - update current value to reflect latest data
                    // but preserve original value for delta calculations
                    // Only update if the row is not modified or confirmed
                    const isModified = self.isRowModified(key);
                    const isConfirmed = self.isRowConfirmed(key);
                    
                    if (!isModified && !isConfirmed) {
                        modifiedRows[key] = {
                            ...modifiedRows[key],
                            currentValue: currentQuantity
                        };
                    }
                }
            });
            self.setState({parts: parts.filter(x => x.part.isActive), modifiedRows});
        });
    };

    handleTabChange = (event, newValue) => {
        this.setState({
            activeTab: newValue,
            page: 0 // Reset to first page when switching tabs
        }, () => {
            this.saveStateToStorage();
        });
    };

    searchInventory = (event) => {
        const searchInput = event.target.value;
        this.setState({
            searchTerm: searchInput,
            page: 0 // Reset to first page when search changes
        });
    };

    handleQuantityChange = (rowKey, newValue) => {
        const modifiedRows = {...this.state.modifiedRows};
        const confirmedRows = {...this.state.confirmedRows};
        const selectedRows = {...this.state.selectedRows};
        const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
        
        const newValueParsed = parseNumberInputValue(newValue);
        
        // If quantity changed after being confirmed, remove confirmation and update originalValue
        if (confirmedRows[rowKey] && modifiedRows[rowKey].originalValue !== newValueParsed) {
            confirmedRows[rowKey] = false;
            // Update originalValue to current value for correction logs
            modifiedRows[rowKey] = {
                ...modifiedRows[rowKey],
                originalValue: modifiedRows[rowKey].currentValue,
                currentValue: newValueParsed
            };
        } else {
            // Normal quantity change
            modifiedRows[rowKey] = {
                ...modifiedRows[rowKey],
                currentValue: newValueParsed
            };
        }
        
        // Mark as manually modified by user
        manuallyModifiedRows[rowKey] = true;
        
        // Auto-select row when quantity changes and row turns yellow (modified)
        if (modifiedRows[rowKey].originalValue !== modifiedRows[rowKey].currentValue) {
            selectedRows[rowKey] = true;
        }
        
        this.setState({modifiedRows, confirmedRows, selectedRows, manuallyModifiedRows}, () => {
            this.saveStateToStorage();
        });
    };

    handleTabKeyPress = (event, currentRowKey, currentTabIndex) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            
            // Get filtered and sorted rows for the current tab (same logic as render method)
            let filteredRows = [];
            
            if (currentTabIndex === 0) {
                // Assemblies - same filtering and sorting as render method
                const filteredAssemblies = [...this.state.assemblies].sort((a, b) => {
                    const aId = a.assembly.assemblyId || '';
                    const bId = b.assembly.assemblyId || '';
                    if (aId !== bId) {
                        return aId.localeCompare(bId);
                    }
                    const aRev = a.assembly.revision || '';
                    const bRev = b.assembly.revision || '';
                    return aRev.localeCompare(bRev);
                }).filter(assembly => {
                    const rowKey = `assembly|${assembly.assembly.id}`;
                    // Apply hide confirmed rows filter
                    if (this.state.hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                        return false;
                    }
                    
                    // Apply search filter
                    if (this.state.searchTerm) {
                        const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                        if (searchTerms.length > 0) {
                            const searchFilter = searchTerms.every(term => {
                                const searchTerm = term.toLowerCase();
                                const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                                const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                                return idMatch || descriptionMatch;
                            });
                            if (!searchFilter) {
                                return false;
                            }
                        }
                    }
                    
                    return true;
                });
                filteredRows = filteredAssemblies.map(assembly => `assembly|${assembly.assembly.id}`);
                
            } else if (currentTabIndex === 1) {
                // Subassemblies - same filtering and sorting as render method
                const filteredSubAssemblies = [...this.state.subAssemblies].sort((a, b) => {
                    const aId = a.subAssembly.subAssemblyId || '';
                    const bId = b.subAssembly.subAssemblyId || '';
                    if (aId !== bId) {
                        return aId.localeCompare(bId);
                    }
                    const aRev = a.subAssembly.revision || '';
                    const bRev = b.subAssembly.revision || '';
                    return aRev.localeCompare(bRev);
                }).filter(subAssembly => {
                    const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                    // Apply hide confirmed rows filter
                    if (this.state.hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                        return false;
                    }
                    
                    // Apply search filter
                    if (this.state.searchTerm) {
                        const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                        if (searchTerms.length > 0) {
                            const searchFilter = searchTerms.every(term => {
                                const searchTerm = term.toLowerCase();
                                const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                                const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                                return idMatch || descriptionMatch;
                            });
                            if (!searchFilter) {
                                return false;
                            }
                        }
                    }
                    
                    // Check if parents are confirmed
                    const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
                    return parentsConfirmed;
                });
                filteredRows = filteredSubAssemblies.map(subAssembly => `subassembly|${subAssembly.subAssembly.id}`);
                
            } else if (currentTabIndex === 2) {
                // Parts - same filtering and sorting as render method
                const filteredParts = [...this.state.parts].sort((a, b) => {
                    const aId = a.part.partId || '';
                    const bId = b.part.partId || '';
                    if (aId !== bId) {
                        return aId.localeCompare(bId);
                    }
                    const aRev = a.part.revision || '';
                    const bRev = b.part.revision || '';
                    return aRev.localeCompare(bRev);
                }).filter(part => {
                    const rowKey = `part|${part.part.id}`;
                    // Apply hide confirmed rows filter
                    if (this.state.hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                        return false;
                    }
                    
                    // Apply search filter
                    if (this.state.searchTerm) {
                        const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                        if (searchTerms.length > 0) {
                            const searchFilter = searchTerms.every(term => {
                                const searchTerm = term.toLowerCase();
                                const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                                const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                                return idMatch || descriptionMatch;
                            });
                            if (!searchFilter) {
                                return false;
                            }
                        }
                    }
                    
                    // Check if parents are confirmed
                    const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
                    return parentsConfirmed;
                });
                filteredRows = filteredParts.map(part => `part|${part.part.id}`);
            }
            
            // Find current row index
            const currentIndex = filteredRows.indexOf(currentRowKey);
            if (currentIndex !== -1) {
                // Find next row
                const nextIndex = (currentIndex + 1) % filteredRows.length;
                const nextRowKey = filteredRows[nextIndex];
                
                // Focus the next row's quantity field
                setTimeout(() => {
                    const nextInput = document.querySelector(`input[data-row-key="${nextRowKey}"]`);
                    if (nextInput) {
                        nextInput.focus();
                        nextInput.select();
                    } else {
                        // Try to find all inputs with data-row-key to debug
                        const allInputs = document.querySelectorAll('input[data-row-key]');
                    }
                }, 100);
            }
        }
    }

    getItemByRowKey = (rowKey) => {
        const [type, id] = rowKey.split('|');
        if (type === 'assembly') {
            return this.state.assemblies.find(a => a.assembly.id === id);
        } else if (type === 'subassembly') {
            return this.state.subAssemblies.find(sa => sa.subAssembly.id === id);
        } else if (type === 'part') {
            return this.state.parts.find(p => p.part.id === id);
        }
        return null;
    }

    handleRowSelection = (rowKey, checked) => {
        const selectedRows = {...this.state.selectedRows};
        if (checked) {
            selectedRows[rowKey] = true;
        } else {
            delete selectedRows[rowKey];
        }
        this.setState({selectedRows});
    }

    handleConfirmSelected = async () => {
        const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
        
        if (selectedRowKeys.length === 0) {
            window.$alertError("No rows selected");
            return;
        }
        
        if (window.confirm(`Confirm ${selectedRowKeys.length} selected item(s)?`)) {
            const savingRows = {...this.state.savingRows};
            selectedRowKeys.forEach(rowKey => {
                savingRows[rowKey] = true;
            });
            this.setState({savingRows});
            
            try {
                // Process each selected row
                for (const rowKey of selectedRowKeys) {
                    const [type, id] = rowKey.split('|');
                    await this.confirmRow(rowKey, type, id, false);
                }
                
                // Clear selections after successful confirmation
                this.setState({selectedRows: {}});
                window.$alertSuccess(`Confirmed ${selectedRowKeys.length} item(s)`);

                // Refresh collaborative state to get updated logs
                this.loadCollaborativeState();
                this.loadData();
            } catch (error) {
                console.error('Error confirming selected rows:', error);
                window.$alertError('Error confirming selected items');
            } finally {
                // Clear saving state
                const savingRows = {...this.state.savingRows};
                selectedRowKeys.forEach(rowKey => {
                    savingRows[rowKey] = false;
                });
                this.setState({savingRows});
            }
        }
    }

    handleResetSelected = async () => {
        const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
        
        if (selectedRowKeys.length === 0) {
            window.$alertError("No rows selected");
            return;
        }
        
        if (window.confirm(`Reset ${selectedRowKeys.length} selected item(s)?`)) {
            const savingRows = {...this.state.savingRows};
            selectedRowKeys.forEach(rowKey => {
                savingRows[rowKey] = true;
            });
            this.setState({savingRows});
            
            try {
                // Process each selected row
                for (const rowKey of selectedRowKeys) {
                    const [type, id] = rowKey.split('|');
                    await this.unconfirmRow(rowKey, type, id, false);
                }
                
                // Clear selections after successful reset
                this.setState({selectedRows: {}});
                window.$alertSuccess(`Reset ${selectedRowKeys.length} item(s)`);
            } catch (error) {
                console.error('Error resetting selected rows:', error);
                window.$alertError('Error resetting selected items');
            } finally {
                // Clear saving state
                const savingRows = {...this.state.savingRows};
                selectedRowKeys.forEach(rowKey => {
                    savingRows[rowKey] = false;
                });
                this.setState({savingRows});

                setTimeout(() => {
                    this.syncDataFromDatabase();
                }, 1000);
            }
        }
    }

    handleSelectAll = () => {
        const { activeTab, searchTerm, hideConfirmedRows, page, rowsPerPage } = this.state;
        let filteredItems = [];
        
        if (activeTab === 0) {
            // Assemblies
            filteredItems = [...this.state.assemblies].sort((a, b) => {
                const aId = a.assembly.assemblyId || '';
                const bId = b.assembly.assemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.assembly.revision || '';
                const bRev = b.assembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(assembly => {
                const rowKey = `assembly|${assembly.assembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                return true;
            });
        } else if (activeTab === 1) {
            // Subassemblies
            filteredItems = [...this.state.subAssemblies].sort((a, b) => {
                const aId = a.subAssembly.subAssemblyId || '';
                const bId = b.subAssembly.subAssemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.subAssembly.revision || '';
                const bRev = b.subAssembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(subAssembly => {
                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
                return parentsConfirmed;
            });
        } else if (activeTab === 2) {
            // Parts
            filteredItems = [...this.state.parts].sort((a, b) => {
                const aId = a.part.partId || '';
                const bId = b.part.partId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.part.revision || '';
                const bRev = b.part.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(part => {
                const rowKey = `part|${part.part.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
                return parentsConfirmed;
            });
        }
        
        // Get paginated items (only the currently displayed ones)
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedItems = filteredItems.slice(startIndex, endIndex);
        
        // Select all paginated items
        const selectedRows = {...this.state.selectedRows};
        paginatedItems.forEach(item => {
            let rowKey;
            if (activeTab === 0) {
                rowKey = `assembly|${item.assembly.id}`;
            } else if (activeTab === 1) {
                rowKey = `subassembly|${item.subAssembly.id}`;
            } else if (activeTab === 2) {
                rowKey = `part|${item.part.id}`;
            }
            selectedRows[rowKey] = true;
        });
        
        this.setState({selectedRows});
    }

    handleSelectNone = () => {
        const { activeTab, searchTerm, hideConfirmedRows, page, rowsPerPage } = this.state;
        let filteredItems = [];
        
        if (activeTab === 0) {
            // Assemblies
            filteredItems = [...this.state.assemblies].sort((a, b) => {
                const aId = a.assembly.assemblyId || '';
                const bId = b.assembly.assemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.assembly.revision || '';
                const bRev = b.assembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(assembly => {
                const rowKey = `assembly|${assembly.assembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                return true;
            });
        } else if (activeTab === 1) {
            // Subassemblies
            filteredItems = [...this.state.subAssemblies].sort((a, b) => {
                const aId = a.subAssembly.subAssemblyId || '';
                const bId = b.subAssembly.subAssemblyId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.subAssembly.revision || '';
                const bRev = b.subAssembly.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(subAssembly => {
                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
                return parentsConfirmed;
            });
        } else if (activeTab === 2) {
            // Parts
            filteredItems = [...this.state.parts].sort((a, b) => {
                const aId = a.part.partId || '';
                const bId = b.part.partId || '';
                if (aId !== bId) {
                    return aId.localeCompare(bId);
                }
                const aRev = a.part.revision || '';
                const bRev = b.part.revision || '';
                return aRev.localeCompare(bRev);
            }).filter(part => {
                const rowKey = `part|${part.part.id}`;
                if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                    return false;
                }
                if (searchTerm) {
                    const searchTerms = searchTerm.split(' ').filter(term => term.trim() !== '');
                    if (searchTerms.length > 0) {
                        const searchFilter = searchTerms.every(term => {
                            const searchTerm = term.toLowerCase();
                            const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                            const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                            return idMatch || descriptionMatch;
                        });
                        if (!searchFilter) {
                            return false;
                        }
                    }
                }
                const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
                return parentsConfirmed;
            });
        }
        
        // Get paginated items (only the currently displayed ones)
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedItems = filteredItems.slice(startIndex, endIndex);
        
        // Deselect all paginated items
        const selectedRows = {...this.state.selectedRows};
        paginatedItems.forEach(item => {
            let rowKey;
            if (activeTab === 0) {
                rowKey = `assembly|${item.assembly.id}`;
            } else if (activeTab === 1) {
                rowKey = `subassembly|${item.subAssembly.id}`;
            } else if (activeTab === 2) {
                rowKey = `part|${item.part.id}`;
            }
            delete selectedRows[rowKey];
        });
        
        this.setState({selectedRows});
    }

    // Calculator functions
    openCalculator = (rowKey, title) => {
        const modifiedRows = this.state.modifiedRows;
        const currentValue = modifiedRows[rowKey] ? modifiedRows[rowKey].currentValue : 0;
        
        this.setState({
            calculatorOpen: true,
            calculatorRowKey: rowKey,
            calculatorTitle: title,
            calculatorLines: [0],
            calculatorTotal:  0
        });
    };

    closeCalculator = () => {
        this.setState({
            calculatorOpen: false,
            calculatorRowKey: null,
            calculatorTitle: '',
            calculatorLines: [0],
            calculatorTotal: 0
        });
    };

    updateCalculatorLine = (index, value) => {
        const lines = [...this.state.calculatorLines];
        lines[index] = parseNumberInputValue(value);
        const total = lines.reduce((sum, line) => sum + line, 0);
        
        this.setState({
            calculatorLines: lines,
            calculatorTotal: total
        });
    };

    addCalculatorLine = () => {
        this.setState({
            calculatorLines: [...this.state.calculatorLines, 0],
            calculatorTotal: this.state.calculatorTotal + 0
        }, () => {
            // Focus the new input after state update
            setTimeout(() => {
                const newInput = document.querySelector(`input[data-calculator-index="${this.state.calculatorLines.length - 1}"]`);
                if (newInput) {
                    newInput.focus();
                }
            }, 100);
        });
    };

    handleCalculatorKeyPress = (event, index) => {
        if (event.key === 'Tab' && index === this.state.calculatorLines.length - 1) {
            event.preventDefault();
            this.setState({
                calculatorLines: [...this.state.calculatorLines, 0]
            }, () => {
                // Focus the new input after state update
                setTimeout(() => {
                    const newInput = document.querySelector(`input[data-calculator-index="${this.state.calculatorLines.length - 1}"]`);
                    if (newInput) {
                        newInput.focus();
                    }
                }, 100);
            });
        }
    };

    confirmCalculator = () => {
        if (window.confirm("Are you sure you're done?")) {
            this.handleQuantityChange(this.state.calculatorRowKey, this.state.calculatorTotal);
            this.closeCalculator();
            
            // Auto-confirm the row after setting the quantity
            const rowKey = this.state.calculatorRowKey;
            const rowType = rowKey.split('|')[0]; // 'assembly', 'subassembly', or 'part'
            let itemId;
            
            if (rowType === 'assembly') {
                const assembly = this.state.assemblies.find(a => `assembly|${a.assembly.id}` === rowKey);
                itemId = assembly?.assembly.id;
            } else if (rowType === 'subassembly') {
                const subAssembly = this.state.subAssemblies.find(sa => `subassembly|${sa.subAssembly.id}` === rowKey);
                itemId = subAssembly?.subAssembly.id;
            } else if (rowType === 'part') {
                const part = this.state.parts.find(p => `part|${p.part.id}` === rowKey);
                itemId = part?.part.id;
            }
            
            if (itemId) {
                // Small delay to ensure quantity change is processed first
                setTimeout(() => {
                    this.confirmRow(rowKey, rowType, itemId, true);
                }, 100);
            }
        }
    };

    isRowModified = (rowKey) => {
        // Only consider a row modified if it was manually modified by the user
        return this.state.manuallyModifiedRows[rowKey] === true;
    };

    isRowConfirmed = (rowKey) => {
        return this.state.confirmedRows[rowKey] === true;
    };

    isRowSaving = (rowKey) => {
        return this.state.savingRows[rowKey] === true;
    };

    confirmRow = async (rowKey, type, id, syncAfter) => {
        const self = this;
        const rowData = this.state.modifiedRows[rowKey];
        const delta = rowData.currentValue - rowData.originalValue;

        // Set saving state
        const savingRows = {...this.state.savingRows};
        savingRows[rowKey] = true;
        this.setState({savingRows});

        try {
            // Always create/update logs to mark as confirmed, even if delta is 0
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            const { packageLogs, inventoryLogs } = this.state.collaborativeState;
            
            // Handle package logs for assemblies and subassemblies
            if (type === 'assembly' || type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                
                // Check if a log already exists for this item and session
                const existingLog = packageLogs.find(log => 
                    log.reason === sessionReason &&
                    ((type === 'assembly' && log.assemblyId === id) ||
                     (type === 'subassembly' && log.subAssemblyId === id))
                );
                
                if (existingLog) {
                    // Update existing log quantity (add delta if there is one, or keep at 0 if no change)
                    const updatePayload = {
                        ...existingLog,
                        quantity: existingLog.quantity + delta,
                        logDate: new Date()
                    };
                    await assemblyService.updatePackageLog(updatePayload);
                } else {
                    // Create new log (quantity will be 0 if no change, or delta if there is a change)
                    const payload = {
                        id: uuid(),
                        assemblyId: type === 'assembly' ? id : null,
                        subAssemblyId: type === 'subassembly' ? id : null,
                        quantity: delta,
                        logDate: new Date(),
                        checkedBom: false,
                        reason: sessionReason
                    };
                    await assemblyService.createPackageLog(payload);
                }
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                
                // Check if a log already exists for this part and session
                const existingLog = inventoryLogs.find(log => 
                    log.reason === sessionReason && log.partId === id
                );
                
                if (existingLog) {
                    // Update existing log quantity (add delta if there is one, or keep at 0 if no change)
                    const updatePayload = {
                        ...existingLog,
                        quantity: existingLog.quantity + delta,
                        logDate: new Date()
                    };
                    await partService.updateInventoryLog(updatePayload);
                } else {
                    // Create new log (quantity will be 0 if no change, or delta if there is a change)
                    const payload = {
                        id: uuid(),
                        quantity: delta,
                        reason: sessionReason,
                        partId: id,
                        logDate: new Date(),
                        userId: AuthenticationService.getInstance().getUserId()
                    };
                    await partService.createInventoryLog(payload);
                }
            }

            // Mark row as confirmed and update original value for future delta calculations
            const confirmedRows = {...this.state.confirmedRows};
            const modifiedRows = {...this.state.modifiedRows};
            const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
            confirmedRows[rowKey] = true;
            // Clear manual modification flag since it's now confirmed
            manuallyModifiedRows[rowKey] = false;
            modifiedRows[rowKey].originalValue = modifiedRows[rowKey].currentValue;
            
            this.setState({confirmedRows, modifiedRows, manuallyModifiedRows}, () => {
                this.saveStateToStorage();
            });

            const selectedRows = {...this.state.selectedRows};
            delete selectedRows[rowKey];
            this.setState({selectedRows});
                
            if (syncAfter) {
                // Refresh collaborative state to get updated logs
                this.loadCollaborativeState();
                this.loadData();
            }

            window.$alertSuccess(`${type} quantity confirmed`);
        } catch (error) {
            console.error('Error confirming row:', error);
            window.$alertError(`Error confirming ${type}`);
        } finally {
            // Clear saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
        }
    };

    unconfirmRow = async (rowKey, type, id, refresh = true) => {
        const self = this;

        if (refresh) {
            const confirm = window.confirm("Are you sure you want to reset this item?");
            if (!confirm) {
                return;
            }
        }

        // Set saving state
        const savingRows = {...this.state.savingRows};
        savingRows[rowKey] = true;
        this.setState({savingRows});

        try {
            const sessionId = this.getSessionId();
            const sessionReason = `Inventory Session ${sessionId}`;
            const { packageLogs, inventoryLogs } = this.state.collaborativeState;
            
            // Handle package logs for assemblies and subassemblies
            if (type === 'assembly' || type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                
                // Find existing log for this item and session
                const existingLog = packageLogs.find(log => 
                    log.reason === sessionReason &&
                    ((type === 'assembly' && log.assemblyId === id) ||
                     (type === 'subassembly' && log.subAssemblyId === id))
                );
                
                if (existingLog) {
                    // Delete the log entirely to unconfirm the row
                    await assemblyService.deletePackageLog(existingLog);
                }
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                
                // Find existing log for this part and session
                const existingLog = inventoryLogs.find(log => 
                    log.reason === sessionReason && log.partId === id
                );
                
                if (existingLog) {
                    // Delete the log entirely to unconfirm the row
                    await partService.deleteInventoryLog(existingLog.id);
                }
            }

        } catch (error) {
            console.error('Error resetting row:', error);
            window.$alertError('Error resetting item');
        } finally {
            // Clear saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
            // Mark row as unconfirmed and reset to original state
            const confirmedRows = {...this.state.confirmedRows};
            const modifiedRows = {...this.state.modifiedRows};
            const manuallyModifiedRows = {...this.state.manuallyModifiedRows};
            confirmedRows[rowKey] = false;
            // Reset to original value and clear manual modification flag
            manuallyModifiedRows[rowKey] = false;
            
            this.setState({confirmedRows, modifiedRows, manuallyModifiedRows}, () => {
                this.saveStateToStorage();
            });

            if (refresh) {
                window.$alertSuccess('Item reset successfully. Quantity will update momentarily...');
                setTimeout(() => {
                    this.syncDataFromDatabase();
                }, 1000);
            }
        }
    }

    setAsNonInventoryItem = async (rowKey, type, id) => {
        if (!window.confirm(`Set this ${type} as a non-inventory item?`)) {
            return;
        }

        try {
            // Get the full item object from state
            const item = this.getItemByRowKey(rowKey);
            if (!item) {
                throw new Error(`Item not found: ${rowKey}`);
            }

            // Set saving state
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = true;
            this.setState({savingRows});

            if (type === 'assembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                const updatedAssembly = {
                    ...item.assembly,
                    isInventoryItem: false
                };
                await assemblyService.updateAssembly(updatedAssembly);
            } else if (type === 'subassembly') {
                const assemblyService = ServiceLocator.Instance.getAssemblyService();
                const updatedSubAssembly = {
                    ...item.subAssembly,
                    isInventoryItem: false
                };
                await assemblyService.updateSubAssembly(updatedSubAssembly);
            } else if (type === 'part') {
                const partService = ServiceLocator.Instance.getPartService();
                const updatedPart = {
                    ...item.part,
                    isInventoryItem: false
                };
                await partService.updatePart(updatedPart);
            }

            // Refresh data to get updated isInventoryItem values
            this.loadData();
            window.$alertSuccess(`${type} set as inventory item`);
            savingRows[rowKey] = false;
            this.setState({savingRows});
        } catch (error) {
            console.error('Error setting as inventory item:', error);
            window.$alertError(`Error setting ${type} as inventory item`);
            const savingRows = {...this.state.savingRows};
            savingRows[rowKey] = false;
            this.setState({savingRows});
        }
    };

    getRowStyle = (rowKey) => {
        if (this.isRowConfirmed(rowKey) && !this.isRowModified(rowKey)) {
            return {backgroundColor: '#d4edda'};
        } else if (this.isRowModified(rowKey)) {
            return {backgroundColor: '#fff3cd'};
        }
        return {};
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage });
    };

    handleChangeRowsPerPage = (event) => {
        this.setState({
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        });
    };

    getPaginatedData = (data) => {
        const { page, rowsPerPage } = this.state;
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return data.slice(startIndex, endIndex);
    };

    renderAssembliesTable = (filteredAssemblies) => {
        const paginatedAssemblies = this.getPaginatedData(filteredAssemblies);

        return (
            <div>
                <div className="parts-table-container">
                    <table className="parts-table">
                        <thead>
                            <tr>
                                <th style={{minWidth: 50}}>
                                    <Checkbox
                                        checked={filteredAssemblies.length > 0 && filteredAssemblies.every(assembly => {
                                            const rowKey = `assembly|${assembly.assembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        indeterminate={filteredAssemblies.some(assembly => {
                                            const rowKey = `assembly|${assembly.assembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        }) && !filteredAssemblies.every(assembly => {
                                            const rowKey = `assembly|${assembly.assembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const selectedRows = {...this.state.selectedRows};
                                            filteredAssemblies.forEach(assembly => {
                                                const rowKey = `assembly|${assembly.assembly.id}`;
                                                if (checked) {
                                                    selectedRows[rowKey] = true;
                                                } else {
                                                    delete selectedRows[rowKey];
                                                }
                                            });
                                            this.setState({selectedRows});
                                        }}
                                    />
                                </th>
                                <th style={{minWidth: 120}}>Part Number</th>
                                <th style={{minWidth: 80}}>Revision</th>
                                <th style={{minWidth: 350}}>Description</th>
                                <th style={{minWidth: 120}}>Assembled On Hand</th>
                                <th style={{minWidth: 120}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedAssemblies.map((assembly) => {
                                const rowKey = `assembly|${assembly.assembly.id}`;
                                const rowData = this.state.modifiedRows[rowKey];
                                return (
                                    <tr key={assembly.assembly.id} style={this.getRowStyle(rowKey)}>
                                        <td>
                                            <Checkbox
                                                checked={this.state.selectedRows[rowKey] || false}
                                                onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            />
                                        </td>
                                        <td>{assembly.assembly.assemblyId}</td>
                                        <td>{assembly.assembly.revision}</td>
                                        <td className="description-ellipsis text-left">{assembly.assembly.description}</td>
                                        <td>
                                            <div style={{alignItems: 'center'}}>
                                                <TextField
                                                    type="number"
                                                    value={rowData ? rowData.currentValue : assembly.totalAssembledOnHandWithRev}
                                                    onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{width: 100}}
                                                    inputProps={{ min: 0, 'data-row-key': rowKey }}
                                                    onKeyDown={(e) => this.handleTabKeyPress(e, rowKey, 0)}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.openCalculator(rowKey, `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}`)}
                                                    style={{padding: 4}}
                                                >
                                                    <FontAwesomeIcon icon={faCalculator} size="sm" />
                                                </IconButton>
                                            </div>
                                        </td>
                                        <td>
                                            {this.isRowSaving(rowKey) ? 
                                                <CircularProgress size={20} />
                                                :
                                            <div style={{alignItems: 'center'}}>
                                                {this.isRowConfirmed(rowKey) ? (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() => this.unconfirmRow(rowKey, 'assembly', assembly.assembly.id)}
                                                            className="warning-button-sm"
                                                            size="small"
                                                            startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                        >
                                                            Reset
                                                        </Button>
                                                        {this.isRowModified(rowKey) && (
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={() => this.confirmRow(rowKey, 'assembly', assembly.assembly.id, true)}
                                                                className="success-button-sm"
                                                                style={{minWidth: 90, marginLeft: '8px'}}
                                                                startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                            >
                                                                Update
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => this.confirmRow(rowKey, 'assembly', assembly.assembly.id, true)}
                                                        className="success-button-sm"
                                                        style={{minWidth: 90}}
                                                        startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                    >
                                                        Confirm
                                                    </Button>
                                                )}
                                                {assembly.assembly.isInventoryItem && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => this.setAsNonInventoryItem(rowKey, 'assembly', assembly.assembly.id)}
                                                        style={{color: '#f44336', marginLeft: '8px'}}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </IconButton>
                                                )}
                                            </div>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <TablePagination
                    component="div"
                    count={filteredAssemblies.length}
                    page={this.state.page}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    rowsPerPage={this.state.rowsPerPage}
                    rowsPerPageOptions={this.state.rowsPerPageOptions}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    style={{display: 'block', margin: '0 auto', width: 410}}
                />
            </div>
        );
    };

    renderSubAssembliesTable = (filteredSubAssemblies) => {
        const paginatedSubAssemblies = this.getPaginatedData(filteredSubAssemblies);

        return (
            <div>
                <div className="parts-table-container">
                    <table className="parts-table">
                        <thead>
                            <tr>
                                <th style={{minWidth: 50}}>
                                    <Checkbox
                                        checked={filteredSubAssemblies.length > 0 && filteredSubAssemblies.every(subAssembly => {
                                            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        indeterminate={filteredSubAssemblies.some(subAssembly => {
                                            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        }) && !filteredSubAssemblies.every(subAssembly => {
                                            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const selectedRows = {...this.state.selectedRows};
                                            filteredSubAssemblies.forEach(subAssembly => {
                                                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                                                if (checked) {
                                                    selectedRows[rowKey] = true;
                                                } else {
                                                    delete selectedRows[rowKey];
                                                }
                                            });
                                            this.setState({selectedRows});
                                        }}
                                    />
                                </th>
                                <th style={{minWidth: 120}}>Part Number</th>
                                <th style={{minWidth: 80}}>Revision</th>
                                <th style={{minWidth: 350}}>Description</th>
                                <th style={{minWidth: 120}}>Assembled On Hand</th>
                                <th style={{minWidth: 120}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedSubAssemblies.map((subAssembly) => {
                                const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
                                const rowData = this.state.modifiedRows[rowKey];
                                return (
                                    <tr key={subAssembly.subAssembly.id} style={this.getRowStyle(rowKey)}>
                                        <td>
                                            <Checkbox
                                                checked={this.state.selectedRows[rowKey] || false}
                                                onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            />
                                        </td>
                                        <td>{subAssembly.subAssembly.subAssemblyId}</td>
                                        <td>{subAssembly.subAssembly.revision}</td>
                                        <td className="description-ellipsis text-left">{subAssembly.subAssembly.description}</td>
                                        <td>
                                            <div style={{alignItems: 'center'}}>
                                                <TextField
                                                    type="number"
                                                    value={rowData ? rowData.currentValue : 0}
                                                    onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{width: 100}}
                                                    inputProps={{ min: 0, 'data-row-key': rowKey }}
                                                    onKeyDown={(e) => this.handleTabKeyPress(e, rowKey, 1)}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.openCalculator(rowKey, `${subAssembly.subAssembly.subAssemblyId} rev ${subAssembly.subAssembly.revision}`)}
                                                    style={{padding: 4}}
                                                >
                                                    <FontAwesomeIcon icon={faCalculator} size="sm" />
                                                </IconButton>
                                            </div>
                                        </td>
                                        <td>
                                            {this.isRowSaving(rowKey) ? 
                                                <CircularProgress size={20} />
                                                :
                                                <div style={{alignItems: 'center'}}>
                                                    {this.isRowConfirmed(rowKey) ? (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => this.unconfirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                                className="warning-button-sm"
                                                                size="small"
                                                                startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                            >
                                                                Reset
                                                            </Button>
                                                            {this.isRowModified(rowKey) && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={() => this.confirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id, true)}
                                                                    className="success-button-sm"
                                                                    style={{minWidth: 90, marginLeft: '8px'}}
                                                                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                                >
                                                                    Update
                                                                </Button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => this.confirmRow(rowKey, 'subassembly', subAssembly.subAssembly.id, true)}
                                                            className="success-button-sm"
                                                            style={{minWidth: 90}}
                                                            startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    )}
                                                    {subAssembly.subAssembly.isInventoryItem && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => this.setAsNonInventoryItem(rowKey, 'subassembly', subAssembly.subAssembly.id)}
                                                            style={{color: '#f44336', marginLeft: '8px'}}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <TablePagination
                    component="div"
                    count={filteredSubAssemblies.length}
                    page={this.state.page}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    rowsPerPage={this.state.rowsPerPage}
                    rowsPerPageOptions={this.state.rowsPerPageOptions}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    style={{display: 'block', margin: '0 auto', width: 410}}
                />
            </div>
        );
    };

    renderPartsTable = (filteredParts) => {
        const paginatedParts = this.getPaginatedData(filteredParts);

        return (
            <div>
                <div className="parts-table-container">
                    <table className="parts-table">
                        <thead>
                            <tr>
                                <th style={{minWidth: 50}}>
                                    <Checkbox
                                        checked={filteredParts.length > 0 && filteredParts.every(part => {
                                            const rowKey = `part|${part.part.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        indeterminate={filteredParts.some(part => {
                                            const rowKey = `part|${part.part.id}`;
                                            return this.state.selectedRows[rowKey];
                                        }) && !filteredParts.every(part => {
                                            const rowKey = `part|${part.part.id}`;
                                            return this.state.selectedRows[rowKey];
                                        })}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            const selectedRows = {...this.state.selectedRows};
                                            filteredParts.forEach(part => {
                                                const rowKey = `part|${part.part.id}`;
                                                if (checked) {
                                                    selectedRows[rowKey] = true;
                                                } else {
                                                    delete selectedRows[rowKey];
                                                }
                                            });
                                            this.setState({selectedRows});
                                        }}
                                    />
                                </th>
                                <th style={{minWidth: 120}}>Part Number</th>
                                <th style={{minWidth: 80}}>Revision</th>
                                <th style={{minWidth: 350}}>Description</th>
                                <th style={{minWidth: 120}}>Total Unpackaged</th>
                                <th style={{minWidth: 120}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedParts.map((part) => {
                                const rowKey = `part|${part.part.id}`;
                                const rowData = this.state.modifiedRows[rowKey];
                                return (
                                    <tr key={part.part.id} style={this.getRowStyle(rowKey)}>
                                        <td>
                                            <Checkbox
                                                checked={this.state.selectedRows[rowKey] || false}
                                                onChange={(e) => this.handleRowSelection(rowKey, e.target.checked)}
                                            />
                                        </td>
                                        <td>{part.part.partId}</td>
                                        <td>{part.part.revision}</td>
                                        <td className="description-ellipsis text-left">{part.part.description}</td>
                                        <td>
                                            <div style={{alignItems: 'center'}}>
                                                <TextField
                                                    type="number"
                                                    value={rowData ? rowData.currentValue : 0}
                                                    onChange={(e) => this.handleQuantityChange(rowKey, e.target.value)}
                                                    variant="outlined"
                                                    size="small"
                                                    style={{width: 100}}
                                                    inputProps={{ min: 0, 'data-row-key': rowKey }}
                                                    onKeyDown={(e) => this.handleTabKeyPress(e, rowKey, 2)}
                                                    onFocus={(e) => e.target.select()}
                                                />
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.openCalculator(rowKey, `${part.part.partId} rev ${part.part.revision}`)}
                                                    style={{padding: 4}}
                                                >
                                                    <FontAwesomeIcon icon={faCalculator} size="sm" />
                                                </IconButton>
                                            </div>
                                        </td>
                                        <td>
                                            {this.isRowSaving(rowKey) ? 
                                                <CircularProgress size={20} />
                                                :
                                                <div style={{alignItems: 'center'}}>
                                                    {this.isRowConfirmed(rowKey) ? (
                                                        <>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => this.unconfirmRow(rowKey, 'part', part.part.id)}
                                                                className="warning-button-sm"
                                                                size="small"
                                                                startIcon={<FontAwesomeIcon icon={faUndo} />}
                                                            >
                                                                Reset
                                                            </Button>
                                                            {this.isRowModified(rowKey) && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={() => this.confirmRow(rowKey, 'part', part.part.id, true)}
                                                                    className="success-button-sm"
                                                                    style={{minWidth: 90, marginLeft: '8px'}}
                                                                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                                >
                                                                    Update
                                                                </Button>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => this.confirmRow(rowKey, 'part', part.part.id, true)}
                                                            className="success-button-sm"
                                                            style={{minWidth: 90}}
                                                            startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                        >
                                                            Confirm
                                                        </Button>
                                                    )}
                                                    {part.part.isInventoryItem && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => this.setAsNonInventoryItem(rowKey, 'part', part.part.id)}
                                                            style={{color: '#f44336', marginLeft: '8px'}}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <TablePagination
                    component="div"
                    count={filteredParts.length}
                    page={this.state.page}
                    onPageChange={this.handleChangePage}
                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                    rowsPerPage={this.state.rowsPerPage}
                    rowsPerPageOptions={this.state.rowsPerPageOptions}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                    style={{display: 'block', margin: '0 auto', width: 410}}
                />
            </div>
        );
    };

    getSessionId = () => {
        return this.props.match?.params?.sessionId || 'Unknown';
    };

    copyUrl = () => {
        navigator.clipboard.writeText(window.location.href);
        window.$alertSuccess('URL copied to clipboard');
    };

    areAllItemsConfirmed = () => {
        const { assemblies, subAssemblies, parts } = this.state;
        
        // Check if all assemblies are confirmed
        for (const assembly of assemblies) {
            const rowKey = `assembly|${assembly.assembly.id}`;
            if (!this.state.confirmedRows[rowKey]) {
                return false;
            }
        }
        
        // Check if all sub-assemblies are confirmed
        for (const subAssembly of subAssemblies) {
            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
            if (!this.state.confirmedRows[rowKey]) {
                return false;
            }
        }
        
        // Check if all parts are confirmed
        for (const part of parts) {
            const rowKey = `part|${part.part.id}`;
            if (!this.state.confirmedRows[rowKey]) {
                return false;
            }
        }
        
        return true;
    };

    confirmAndEndSession = () => {
        localStorage.removeItem('inventorySetupState' + this.getSessionId());
        globalProps.history.push('/admin/inventory');
    };

    render() {
        const { classes } = this.props;
        const { loading, activeTab, assemblies, subAssemblies, parts, hideConfirmedRows } = this.state;
        const sessionId = this.getSessionId();

        if (loading) {
            return (
                <DashboardTemplate title={`Inventory Session ${sessionId}`} onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress/>
                    </div>
                </DashboardTemplate>
            );
        }

        // Filter and sort assemblies
        let filteredAssemblies = [...assemblies].sort((a, b) => {
            const aId = a.assembly.assemblyId || '';
            const bId = b.assembly.assemblyId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.assembly.revision || '';
            const bRev = b.assembly.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(assembly => {
            const rowKey = `assembly|${assembly.assembly.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !assembly.assembly.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = assembly.assembly.assemblyId && assembly.assembly.assemblyId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = assembly.assembly.description && assembly.assembly.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Assemblies are always top-level, so always show them (no parent check needed)
            return true;
        });

        // Filter and sort subassemblies
        let filteredSubAssemblies = [...subAssemblies].sort((a, b) => {
            const aId = a.subAssembly.subAssemblyId || '';
            const bId = b.subAssembly.subAssemblyId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.subAssembly.revision || '';
            const bRev = b.subAssembly.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(subAssembly => {
            const rowKey = `subassembly|${subAssembly.subAssembly.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !subAssembly.subAssembly.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = subAssembly.subAssembly.subAssemblyId && subAssembly.subAssembly.subAssemblyId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = subAssembly.subAssembly.description && subAssembly.subAssembly.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Check if parents are confirmed (only show if parents are confirmed or if it's top-level)
            const parentsConfirmed = this.areParentsConfirmed('subassembly', subAssembly.subAssembly.id);
            return parentsConfirmed;
        });

        // Filter and sort parts
        let filteredParts = [...parts].sort((a, b) => {
            const aId = a.part.partId || '';
            const bId = b.part.partId || '';
            if (aId !== bId) {
                return aId.localeCompare(bId);
            }
            const aRev = a.part.revision || '';
            const bRev = b.part.revision || '';
            return aRev.localeCompare(bRev);
        }).filter(part => {
            const rowKey = `part|${part.part.id}`;
            // Apply hide confirmed rows filter
            if (hideConfirmedRows && this.isRowConfirmed(rowKey)) {
                return false;
            }
            
            // Apply inventory item filter
            if (!this.state.showNonInventoryItems && !part.part.isInventoryItem) {
                return false;
            }
            
            // Apply search filter
            if (this.state.searchTerm) {
                const searchTerms = this.state.searchTerm.split(' ').filter(term => term.trim() !== '');
                if (searchTerms.length > 0) {
                    const searchFilter = searchTerms.every(term => {
                        const searchTerm = term.toLowerCase();
                        const idMatch = part.part.partId && part.part.partId.toLowerCase().includes(searchTerm);
                        const descriptionMatch = part.part.description && part.part.description.toLowerCase().includes(searchTerm);
                        return idMatch || descriptionMatch;
                    });
                    if (!searchFilter) {
                        return false;
                    }
                }
            }
            
            // Check if parents are confirmed (only show if parents are confirmed or if it's top-level)
            const parentsConfirmed = this.areParentsConfirmed('part', part.part.id);
            return parentsConfirmed;
        });

        // Calculate counts from filtered data
        const assemblyCount = filteredAssemblies.length;
        const subAssemblyCount = filteredSubAssemblies.length;
        const partCount = filteredParts.length;

        return (
            <DashboardTemplate title={`Inventory Session ${sessionId}`} onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className="container" style={{maxWidth: '1200px'}}>
                        {/* Search Bar and Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <TextField
                                value={this.state.searchTerm || ''}
                                onChange={this.searchInventory}
                                label="Search PN or Description..."
                                variant="outlined"
                                className="search-field"
                                style={{minWidth: '300px'}}
                                inputRef={ref => this.searchInputRef = ref}
                            />
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className="success-button-lg"
                                    onClick={this.syncDataFromDatabase}
                                    disabled={this.state.syncing}
                                    style={{minWidth: 120}}
                                    startIcon={this.state.syncing ? <CircularProgress size={16} /> : <FontAwesomeIcon icon={faSync} />}
                                >
                                    {this.state.syncing ? 'Syncing...' : 'Sync Database'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.copyUrl}
                                    className="success-button-lg"
                                    style={{minWidth: 120}}
                                >
                                    Copy URL
                                </Button>
                            </div>
                        </Box>
                        
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                            <Tabs value={activeTab} onChange={this.handleTabChange} aria-label="inventory setup tabs">
                                <Tab 
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Assemblies
                                            <Chip size="small" label={assemblyCount} />
                                        </Box>
                                    } 
                                />
                                <Tab 
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Subassemblies
                                            <Chip size="small" label={subAssemblyCount} />
                                        </Box>
                                    } 
                                />
                                <Tab 
                                    label={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            Parts
                                            <Chip size="small" label={partCount} />
                                        </Box>
                                    } 
                                />
                            </Tabs>
                        </Box>

                        {/* Toolbar with filters */}
                        <div className={classes.toolbar} style={{marginBottom: '16px', borderBottom: '1px solid #e0e0e0', paddingBottom: '16px'}}>
                            <div className={classes.toolbarLeft}>
                                <Typography variant="body2" color="textSecondary">
                                    {this.state.collaborativeState.lastRefresh && 
                                        `Last sync: ${this.state.collaborativeState.lastRefresh.toLocaleTimeString()}`
                                    }
                                </Typography>
                                {this.state.syncing && <div style={{marginLeft: 10, fontSize: 18, fontWeight: 800}}><CircularProgress size={18} /> Syncing...</div>}
                            </div>
                            <div className={classes.toolbarRight}>
                                <div className={classes.selectButtons}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={this.handleSelectAll}
                                        style={{minWidth: 80}}
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={this.handleSelectNone}
                                        style={{minWidth: 80}}
                                    >
                                        Select None
                                    </Button>
                                </div>
                                <div className={`${classes.filterToggle} filter-toggle`} onClick={this.toggleHideConfirmedRows}>
                                    <Checkbox
                                        checked={this.state.hideConfirmedRows}
                                        onChange={this.toggleHideConfirmedRows}
                                    />
                                    <span className={classes.filterLabel}>Hide Confirmed Rows</span>
                                </div>
                                <div className={`${classes.filterToggle} filter-toggle`} onClick={this.toggleShowNonInventoryItems}>
                                    <Checkbox
                                        checked={this.state.showNonInventoryItems}
                                        onChange={this.toggleShowNonInventoryItems}
                                    />
                                    <span className={classes.filterLabel}>Show Non-Inventory Items</span>
                                </div>
                            </div>
                        </div>

                        <TabPanel value={activeTab} index={0}>
                            {this.renderAssembliesTable(filteredAssemblies)}
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            {this.renderSubAssembliesTable(filteredSubAssemblies)}
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            {this.renderPartsTable(filteredParts)}
                        </TabPanel>

                        {/* Confirm & End Session Button */}
                        {this.areAllItemsConfirmed() && (
                            <div style={{margin: 20}}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.confirmAndEndSession}
                                    className="success-button-lg"
                                    style={{minWidth: 200, fontSize: '16px'}}
                                >
                                    Confirm & End Session
                                </Button>
                            </div>
                        )}

                    </div>
                </div>

                {/* Floating Action Button for Selected Rows */}
                {Object.keys(this.state.selectedRows).some(key => this.state.selectedRows[key]) && (() => {
                    const selectedRowKeys = Object.keys(this.state.selectedRows).filter(key => this.state.selectedRows[key]);
                    const allConfirmed = selectedRowKeys.length > 0 && selectedRowKeys.every(key => this.isRowConfirmed(key));
                    
                    return (
                        <Fab
                            color={allConfirmed ? "secondary" : "primary"}
                            className={classes.floatingConfirmButton}
                            onClick={allConfirmed ? this.handleResetSelected : this.handleConfirmSelected}
                            variant="extended"
                            size="large"
                        >
                            <FontAwesomeIcon icon={faCheck} style={{marginRight: '8px'}} />
                            {allConfirmed ? 'Reset Selected' : 'Confirm Selected'} ({selectedRowKeys.length})
                        </Fab>
                    );
                })()}

                {/* Calculator Modal */}
                <Dialog 
                    open={this.state.calculatorOpen} 
                    onClose={this.closeCalculator}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle>
                        Calculator
                        <Typography variant="subtitle2" style={{color: '#666', marginTop: '4px'}}>
                            {this.state.calculatorTitle}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <div style={{marginBottom: '16px'}}>
                            {this.state.calculatorLines.map((line, index) => (
                                <TextField
                                    key={index}
                                    type="number"
                                    value={line === 0 ? '' : line}
                                    onChange={(e) => this.updateCalculatorLine(index, e.target.value)}
                                    onKeyDown={(e) => this.handleCalculatorKeyPress(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    inputProps={{
                                        'data-calculator-index': index,
                                        min: 0,
                                        step: 'any'
                                    }}
                                    variant="outlined"
                                    size="small"
                                    style={{width: '100%', marginBottom: '8px'}}
                                    autoFocus={index === 0}
                                />
                            ))}
                            
                            <Button
                                onClick={this.addCalculatorLine}
                                variant="outlined"
                                size="small"
                                style={{marginTop: '8px'}}
                            >
                                Add Line
                            </Button>
                        </div>
                        
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            marginTop: '16px'
                        }}>
                            <Typography variant="h6" style={{textAlign: 'center', margin: 0}}>
                                Total: {this.state.calculatorTotal}
                            </Typography>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeCalculator}>
                            Cancel
                        </Button>
                        <Button
                            onClick={this.confirmCalculator}
                            variant="contained"
                            color="primary"
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </DashboardTemplate>
        );
    }
}

let globalProps;

InventorySetup.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(InventorySetup);